import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const RegisterContext = createContext(null);

/**
 * Called by RiskIndexDisplay so the footnote block only lists the markers that
 * were actually rendered. Counting live markers (rather than scanning the
 * dataset) is what keeps the note honest with pagination, filtering and lazily
 * expanded trees — a note without its reference on screen is noise.
 *
 * @param {'nd'|'expert'|null} marker
 */
export function useRiskIndexMarker(marker) {
  const register = useContext(RegisterContext);
  useEffect(
    () => (marker && register ? register(marker) : undefined),
    [marker, register]
  );
}

/**
 * Wraps a table / list / card grid and appends the †/†† footnotes for whatever
 * its RiskIndexDisplay descendants rendered. Client spec (CRAAQ) asks for both
 * the reference next to the value *and* a footnote below the table; the
 * tooltip on the reference stays, it serves quick reading, the note serves
 * print and screen readers.
 *
 * Usage: <RiskIndexFootnotes><Table .../></RiskIndexFootnotes>
 */
export default function RiskIndexFootnotes({ children, className = '' }) {
  const { t } = useTranslation('common');
  const [counts, setCounts] = useState({ nd: 0, expert: 0 });

  // Stable identity: the registering effects in children must not re-run.
  const register = useCallback((marker) => {
    setCounts((c) => ({ ...c, [marker]: c[marker] + 1 }));
    return () => setCounts((c) => ({ ...c, [marker]: c[marker] - 1 }));
  }, []);

  return (
    <RegisterContext.Provider value={register}>
      {children}
      {(counts.nd > 0 || counts.expert > 0) && (
        <div
          className={`mt-2 text-[12px] leading-snug text-[#595959] ${className}`.trim()}
        >
          {counts.nd > 0 && (
            <p className="mb-0">
              {'†'} {t('risk_index.nd_footnote')}
            </p>
          )}
          {counts.expert > 0 && (
            <p className="mb-0">
              {'††'} {t('risk_index.expert_footnote')}
            </p>
          )}
        </div>
      )}
    </RegisterContext.Provider>
  );
}
